import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SubscriptionTier as ApiSubscriptionTier, CreateSubscriptionTierInput } from '../../types/subscriptionTier';
import { getTiers, createTier, updateTier, deleteTier } from '../../services/SubscriptionTierService';
import { getSubscriptionSettings as apiGetSettings, updateSubscriptionSettings as apiUpdateSettings } from '../../services/SettingsService';
import { getSubscriptionAnalytics as apiGetAnalytics } from '../../services/AnalyticsService';
import { getUsers, ApiUser } from '../../services/UserService';
import { getSubscriptions, createSubscription as apiCreateSubscription, deleteSubscription as apiDeleteSubscription, Subscription as ApiSubscription } from '../../services/SubscriptionService';
import { 
  CreditCardIcon, 
  UserGroupIcon, 
  ChartBarIcon, 
  BellAlertIcon, 
  ArrowTrendingUpIcon, 
  ArrowTrendingDownIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  CogIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

type TabType = 'subscriptions' | 'tiers' | 'settings' | 'analytics';

// Subscription interface is now imported from SubscriptionService

// Keep local SubscriptionTier for the old card display format if needed, but use ApiSubscriptionTier from service
interface SubscriptionTierCard {
  id: string;
  name: string;
  price: number;
  billingCycle: 'monthly' | 'yearly';
  features: string[];
  status: 'active' | 'inactive';
}

interface NewSubscriptionTier {
  name: string;
  subscriptionPrice: string;
  smsCreditsAnnual: string;
  singleSmsMessages: boolean;
  bulkCampaigns: boolean;
  additionalSmsCreditsAnnual: string;
  additionalSmsCreditsMonthly: string;
  oneTimeSetupFee: string;
  customSenderId: boolean;
  messageTemplates: string;
  users: string;
  sharedTemplates: boolean;
  sharedContacts: boolean;
  scheduledMessages: boolean;
  recurringMessages: boolean;
  usageReporting: boolean;
  scheduledReports: boolean;
  apiAccess: boolean;
  support: string;
  customFields: { name: string; value: string }[];
}

const SubscriptionManagement = () => {
  const [activeTab, setActiveTab] = useState<TabType>('subscriptions');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isAddSubscriptionModalOpen, setIsAddSubscriptionModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<ApiSubscription | null>(null);
  const [selectedTier, setSelectedTier] = useState<ApiSubscriptionTier | null>(null);
  const [editedSubscription, setEditedSubscription] = useState<ApiSubscription | null>(null);
  const [tiers, setTiers] = useState<ApiSubscriptionTier[]>([]);
  const [loadingTiers, setLoadingTiers] = useState(false);
  const [errorTiers, setErrorTiers] = useState<string | null>(null);
  const [users, setUsers] = useState<ApiUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [errorUsers, setErrorUsers] = useState<string | null>(null);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [subscriptions, setSubscriptions] = useState<ApiSubscription[]>([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [errorSubscriptions, setErrorSubscriptions] = useState<string | null>(null);
  const [newSubscription, setNewSubscription] = useState({
    companyName: '',
    contactPersonName: '',
    emailAddress: '',
    contactTelephone: '',
    subscriptionTier: '',
    startDate: '',
    endDate: '',
    amountPerMonth: '',
    autoRenew: '',
    selectedUserIds: [] as string[]
  });
  const [newTier, setNewTier] = useState<NewSubscriptionTier>({
    name: '',
    subscriptionPrice: '',
    smsCreditsAnnual: '',
    singleSmsMessages: true,
    bulkCampaigns: true,
    additionalSmsCreditsAnnual: '',
    additionalSmsCreditsMonthly: '',
    oneTimeSetupFee: '',
    customSenderId: false,
    messageTemplates: '',
    users: '',
    sharedTemplates: false,
    sharedContacts: false,
    scheduledMessages: false,
    recurringMessages: false,
    usageReporting: true,
    scheduledReports: true,
    apiAccess: true,
    support: 'Email',
    customFields: []
  });
  const [newCustomField, setNewCustomField] = useState({ name: '', value: '' });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [settings, setSettings] = useState({
    defaultBillingCycle: 'monthly',
    gracePeriodDays: 7,
    autoRenewalDefault: 'enabled',
    paymentGateway: 'stripe'
  });
  const [analytics, setAnalytics] = useState({
    totalSubscriptions: 0,
    activeSubscriptions: 0,
    churnedSubscriptions: 0,
    mrr: 0,
    arr: 0,
    churnRate: 0,
    averageSubscriptionValue: 0
  });
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Fetch tiers on component mount and when tiers tab is active
  useEffect(() => {
    if (activeTab === 'tiers' || activeTab === 'subscriptions') {
      fetchTiers();
    }
  }, [activeTab]);

  // Fetch subscriptions when subscriptions tab is active (also runs on mount since default tab is 'subscriptions')
  useEffect(() => {
    if (activeTab === 'subscriptions') {
      fetchSubscriptions();
    }
  }, [activeTab]);

  // Fetch users when subscription modal opens or subscriptions tab is active
  useEffect(() => {
    if (activeTab === 'subscriptions' || isAddSubscriptionModalOpen) {
      fetchUsers();
    }
  }, [activeTab, isAddSubscriptionModalOpen]);

  // Load settings when Settings tab becomes active
  useEffect(() => {
    const load = async () => {
      if (activeTab !== 'settings') return;
      try {
        setSettingsLoading(true);
        setSettingsError(null);
        const s = await apiGetSettings();
        setSettings(s);
      } catch (e: any) {
        console.error('Failed to fetch settings:', e);
        setSettingsError(e?.message || 'Failed to load settings');
      } finally {
        setSettingsLoading(false);
      }
    };
    load();
  }, [activeTab]);

  // Load analytics when Analytics tab becomes active
  useEffect(() => {
    const load = async () => {
      if (activeTab !== 'analytics') return;
      try {
        setAnalyticsLoading(true);
        setAnalyticsError(null);
        const a = await apiGetAnalytics();
        setAnalytics(a);
      } catch (e: any) {
        console.error('Failed to fetch analytics:', e);
        setAnalyticsError(e?.message || 'Failed to load analytics');
      } finally {
        setAnalyticsLoading(false);
      }
    };
    load();
  }, [activeTab]);

  const fetchTiers = async () => {
    try {
      setLoadingTiers(true);
      setErrorTiers(null);
      const fetchedTiers = await getTiers(true); // Get all tiers including inactive
      setTiers(fetchedTiers);
    } catch (err) {
      console.error('Failed to fetch tiers:', err);
      setErrorTiers('Failed to load subscription tiers');
    } finally {
      setLoadingTiers(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      setErrorUsers(null);
      const fetchedUsers = await getUsers();
      setUsers(fetchedUsers);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setErrorUsers('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      setLoadingSubscriptions(true);
      setErrorSubscriptions(null);
      
      // Check if user is authenticated - try multiple possible token keys
      const token = localStorage.getItem('token') || 
                    localStorage.getItem('authToken') || 
                    localStorage.getItem('accessToken');
      if (!token) {
        setErrorSubscriptions('Unauthorized: Please log in again. No authentication token found. (Status: 401)');
        setLoadingSubscriptions(false);
        return;
      }
      
      const fetchedSubscriptions = await getSubscriptions();
      console.log('Fetched subscriptions:', fetchedSubscriptions);
      console.log('Number of subscriptions:', fetchedSubscriptions?.length || 0);
      // Handle case where API returns empty array
      setSubscriptions(fetchedSubscriptions || []);
    } catch (err: any) {
      console.error('Failed to fetch subscriptions:', err);
      // Get more detailed error message
      const errorMessage = err?.message || 'Failed to load subscriptions';
      setErrorSubscriptions(errorMessage);
    } finally {
      setLoadingSubscriptions(false);
    }
  };

  // Convert API tiers to card format for display (if needed)
  const subscriptionTiersCards: SubscriptionTierCard[] = tiers.map(tier => {
    const price = parseFloat(tier.priceMonthly.replace('$', '').replace(',', '')) || 0;
    const features: string[] = [];
    if (tier.smsCreditsAnnual) features.push(`${tier.smsCreditsAnnual} SMS credits`);
    if (tier.users) features.push(`${tier.users} users`);
    if (tier.support) features.push(`${tier.support} support`);
    if (tier.apiAccess) features.push('API access');
    return {
      id: tier.id,
      name: tier.name,
      price,
      billingCycle: 'monthly',
      features,
      status: tier.status
    };
  });

  // Mock data for churn risk
  const churnRiskData = [
    { customer: 'Global Services Inc', risk: 'High', lastLogin: '2024-03-10', usage: '40%', paymentHistory: 'Missed 2 payments' },
    { customer: 'Digital Marketing Co', risk: 'Medium', lastLogin: '2024-03-15', usage: '60%', paymentHistory: 'Late payment' },
    { customer: 'Startup Innovations', risk: 'Low', lastLogin: '2024-03-20', usage: '30%', paymentHistory: 'On time' }
  ];

  // Mock data for subscription metrics
  const subscriptionMetrics = {
    totalSubscriptions: 1250,
    activeSubscriptions: 1180,
    churnedSubscriptions: 70,
    mrr: 45678,
    arr: 548136,
    churnRate: 2.4,
    averageSubscriptionValue: 89
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'High':
        return 'bg-red-100 text-red-800';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'Low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleAddCustomField = () => {
    if (newCustomField.name.trim() && newCustomField.value.trim()) {
      setNewTier({
        ...newTier,
        customFields: [...newTier.customFields, { ...newCustomField }]
      });
      setNewCustomField({ name: '', value: '' });
    }
  };

  const handleRemoveCustomField = (index: number) => {
    const updatedCustomFields = [...newTier.customFields];
    updatedCustomFields.splice(index, 1);
    setNewTier({
      ...newTier,
      customFields: updatedCustomFields
    });
  };

  const handleTierSelection = (tierId: string) => {
    const selectedTierObj = tiers.find(tier => tier.id === tierId);
    if (selectedTierObj) {
      const price = parseFloat(selectedTierObj.priceMonthly.replace('$', '').replace(',', '')) || 0;
      setNewSubscription({
        ...newSubscription,
        subscriptionTier: tierId,
        amountPerMonth: price > 0 ? price.toFixed(2) : ''
      });
    } else {
      setNewSubscription({
        ...newSubscription,
        subscriptionTier: tierId,
        amountPerMonth: ''
      });
    }
  };

  const handleUserToggle = (userId: string) => {
    setNewSubscription(prev => {
      const isSelected = prev.selectedUserIds.includes(userId);
      return {
        ...prev,
        selectedUserIds: isSelected
          ? prev.selectedUserIds.filter(id => id !== userId)
          : [...prev.selectedUserIds, userId]
      };
    });
  };

  const handleSelectAllUsers = () => {
    const filteredUsers = getFilteredUsers();
    const allSelected = filteredUsers.every(user => 
      newSubscription.selectedUserIds.includes(user.id)
    );
    
    if (allSelected) {
      // Deselect all filtered users
      const filteredUserIds = filteredUsers.map(user => user.id);
      setNewSubscription(prev => ({
        ...prev,
        selectedUserIds: prev.selectedUserIds.filter(id => !filteredUserIds.includes(id))
      }));
    } else {
      // Select all filtered users that aren't already selected
      const filteredUserIds = filteredUsers.map(user => user.id);
      setNewSubscription(prev => ({
        ...prev,
        selectedUserIds: Array.from(new Set([...prev.selectedUserIds, ...filteredUserIds]))
      }));
    }
  };

  const handleRemoveUser = (userId: string) => {
    setNewSubscription(prev => ({
      ...prev,
      selectedUserIds: prev.selectedUserIds.filter(id => id !== userId)
    }));
  };

  const getFilteredUsers = () => {
    if (!userSearchTerm.trim()) return users;
    const searchLower = userSearchTerm.toLowerCase();
    return users.filter(user => {
      const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
      const email = user.companyEmail.toLowerCase();
      const company = user.company?.toLowerCase() || '';
      return fullName.includes(searchLower) || email.includes(searchLower) || company.includes(searchLower);
    });
  };

  const handleCreateSubscription = async () => {
    // Validate required fields
    if (!newSubscription.companyName || !newSubscription.contactPersonName || 
        !newSubscription.emailAddress || !newSubscription.contactTelephone ||
        !newSubscription.subscriptionTier || !newSubscription.startDate ||
        !newSubscription.endDate || !newSubscription.amountPerMonth || 
        !newSubscription.autoRenew) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const selectedTierObj = tiers.find(t => t.id === newSubscription.subscriptionTier);
      await apiCreateSubscription({
        subscriptionTierId: newSubscription.subscriptionTier || undefined,
        subscriptionTierName: selectedTierObj?.name,
        startDate: newSubscription.startDate,
        endDate: newSubscription.autoRenew?.toLowerCase() === 'yes' ? null : newSubscription.endDate,
        autoRenew: newSubscription.autoRenew,
        selectedUserIds: newSubscription.selectedUserIds,
        billingPeriod: 'monthly'
      });
      
      // Reset form and close modal
      setNewSubscription({
        companyName: '',
        contactPersonName: '',
        emailAddress: '',
        contactTelephone: '',
        subscriptionTier: '',
        startDate: '',
        endDate: '',
        amountPerMonth: '',
        autoRenew: '',
        selectedUserIds: []
      });
      setUserSearchTerm('');
      setIsAddSubscriptionModalOpen(false);
      
      // Show success message
      alert('Subscription(s) created successfully!');
      
      // Refresh subscriptions list
      await fetchSubscriptions();
    } catch (err) {
      console.error('Failed to create subscription:', err);
      alert('Failed to create subscription. Please try again.');
    }
  };

  const handleAddTier = async () => {
    try {
      const tierData: CreateSubscriptionTierInput = {
        name: newTier.name,
        priceMonthly: newTier.subscriptionPrice,
        smsCreditsAnnual: newTier.smsCreditsAnnual,
        additionalSmsCreditPriceAnnual: newTier.additionalSmsCreditsAnnual,
        additionalSmsCreditPriceMonthly: newTier.additionalSmsCreditsMonthly,
        oneTimeSetupFee: newTier.oneTimeSetupFee,
        messageTemplates: newTier.messageTemplates,
        users: newTier.users,
        support: newTier.support,
        singleSmsMessages: newTier.singleSmsMessages,
        bulkCampaigns: newTier.bulkCampaigns,
        customSenderId: newTier.customSenderId,
        sharedTemplates: newTier.sharedTemplates,
        sharedContacts: newTier.sharedContacts,
        scheduledMessages: newTier.scheduledMessages,
        recurringMessages: newTier.recurringMessages,
        usageReporting: newTier.usageReporting,
        scheduledReports: newTier.scheduledReports,
        apiAccess: newTier.apiAccess,
        featuresJson: newTier.customFields.length > 0 ? newTier.customFields : null,
        sortIndex: tiers.length + 1,
        status: 'active'
      };

      await createTier(tierData);
      setIsAddModalOpen(false);
      
      // Refresh tiers list
      await fetchTiers();
      
      // Reset form
      setNewTier({
        name: '',
        subscriptionPrice: '',
        smsCreditsAnnual: '',
        singleSmsMessages: true,
        bulkCampaigns: true,
        additionalSmsCreditsAnnual: '',
        additionalSmsCreditsMonthly: '',
        oneTimeSetupFee: '',
        customSenderId: false,
        messageTemplates: '',
        users: '',
        sharedTemplates: false,
        sharedContacts: false,
        scheduledMessages: false,
        recurringMessages: false,
        usageReporting: true,
        scheduledReports: true,
        apiAccess: true,
        support: 'Email',
        customFields: []
      });
    } catch (err) {
      console.error('Failed to create tier:', err);
      alert('Failed to create subscription tier. Please try again.');
    }
  };

  const handleEditSubscription = (subscription: ApiSubscription) => {
    setSelectedSubscription(subscription);
    setEditedSubscription({ ...subscription });
    setIsEditModalOpen(true);
  };

  const handleSaveSubscription = async () => {
    if (editedSubscription) {
      try {
        // TODO: Implement API call to update the subscription
        console.log('Saving edited subscription:', editedSubscription);
        
        // In a real app, you would call an API like:
        // await updateSubscription(editedSubscription.id, editedSubscription);
        
        // Refresh subscriptions list
        await fetchSubscriptions();
        
        setIsEditModalOpen(false);
        setSelectedSubscription(null);
        setEditedSubscription(null);
      } catch (err) {
        console.error('Failed to save subscription:', err);
        alert('Failed to save subscription. Please try again.');
      }
    }
  };

  const renderAddSubscriptionModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Add New Subscription</h2>
          <button
            onClick={() => {
              setIsAddSubscriptionModalOpen(false);
              // Reset form
              setNewSubscription({
                companyName: '',
                contactPersonName: '',
                emailAddress: '',
                contactTelephone: '',
                subscriptionTier: '',
                startDate: '',
                endDate: '',
                amountPerMonth: '',
                autoRenew: '',
                selectedUserIds: []
              });
              setUserSearchTerm('');
            }}
            className="text-gray-500 hover:text-gray-700"
          >
            <XCircleIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={newSubscription.companyName}
              onChange={(e) => setNewSubscription({ ...newSubscription, companyName: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              placeholder="e.g., Tech Solutions Ltd"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contact Person's Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={newSubscription.contactPersonName}
              onChange={(e) => setNewSubscription({ ...newSubscription, contactPersonName: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              placeholder="e.g., John Doe"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={newSubscription.emailAddress}
              onChange={(e) => setNewSubscription({ ...newSubscription, emailAddress: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              placeholder="e.g., john.doe@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contact Telephone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              value={newSubscription.contactTelephone}
              onChange={(e) => setNewSubscription({ ...newSubscription, contactTelephone: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              placeholder="e.g., +1-555-123-4567"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subscription Tier <span className="text-red-500">*</span>
            </label>
            <select
              value={newSubscription.subscriptionTier}
              onChange={(e) => handleTierSelection(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="">Select a tier</option>
              {tiers.filter(tier => tier.status === 'active').map(tier => (
                <option key={tier.id} value={tier.id}>
                  {tier.name} - {tier.priceMonthly}/month
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subscription Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={newSubscription.startDate}
                onChange={(e) => setNewSubscription({ ...newSubscription, startDate: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subscription End Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={newSubscription.endDate}
                onChange={(e) => setNewSubscription({ ...newSubscription, endDate: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subscription Amount Per Month <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-gray-500">$</span>
              <input
                type="number"
                value={newSubscription.amountPerMonth}
                onChange={(e) => setNewSubscription({ ...newSubscription, amountPerMonth: e.target.value })}
                className="w-full border border-gray-300 rounded-md pl-8 pr-3 py-2"
                placeholder="0.00"
                step="0.01"
                readOnly
                title="Amount is automatically populated based on selected tier"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Automatically populated based on selected tier</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Auto Renew <span className="text-red-500">*</span>
            </label>
            <select
              value={newSubscription.autoRenew}
              onChange={(e) => setNewSubscription({ ...newSubscription, autoRenew: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="">Select an option</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          {/* User Selection Section */}
          <div className="border-t pt-6 mt-6">
            <div className="flex justify-between items-center mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Select Users <span className="text-gray-500 font-normal">(Optional)</span>
              </label>
              {getFilteredUsers().length > 0 && (
                <button
                  type="button"
                  onClick={handleSelectAllUsers}
                  className="text-sm text-[#FF5722] hover:text-[#FF5722]/80"
                >
                  {getFilteredUsers().every(user => newSubscription.selectedUserIds.includes(user.id))
                    ? 'Deselect All'
                    : 'Select All'}
                </button>
              )}
            </div>

            {/* Selected Users Display */}
            {newSubscription.selectedUserIds.length > 0 && (
              <div className="mb-4">
                <p className="text-xs text-gray-600 mb-2">
                  Selected: {newSubscription.selectedUserIds.length} user(s)
                </p>
                <div className="flex flex-wrap gap-2">
                  {newSubscription.selectedUserIds.map(userId => {
                    const user = users.find(u => u.id === userId);
                    if (!user) return null;
                    return (
                      <span
                        key={userId}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-[#FF5722]/10 text-[#FF5722] border border-[#FF5722]/20"
                      >
                        {user.firstName} {user.lastName}
                        <button
                          type="button"
                          onClick={() => handleRemoveUser(userId)}
                          className="ml-2 text-[#FF5722] hover:text-[#FF5722]/80"
                        >
                          <XCircleIcon className="w-4 h-4" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Search Users */}
            <div className="mb-4">
              <input
                type="text"
                value={userSearchTerm}
                onChange={(e) => setUserSearchTerm(e.target.value)}
                placeholder="Search users by name, email, or company..."
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
            </div>

            {/* Users List */}
            <div className="border border-gray-200 rounded-md max-h-60 overflow-y-auto">
              {loadingUsers ? (
                <div className="p-4 text-center text-gray-500 text-sm">Loading users...</div>
              ) : errorUsers ? (
                <div className="p-4 text-center text-red-500 text-sm">{errorUsers}</div>
              ) : getFilteredUsers().length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">
                  {userSearchTerm ? 'No users found matching your search' : 'No users available'}
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {getFilteredUsers().map((user) => {
                    const isSelected = newSubscription.selectedUserIds.includes(user.id);
                    return (
                      <label
                        key={user.id}
                        className={`flex items-center p-3 hover:bg-gray-50 cursor-pointer ${
                          isSelected ? 'bg-[#FF5722]/5' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleUserToggle(user.id)}
                          className="h-4 w-4 text-[#FF5722] rounded border-gray-300 focus:ring-[#FF5722]"
                        />
                        <div className="ml-3 flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="text-xs text-gray-500">{user.companyEmail}</div>
                          {user.company && (
                            <div className="text-xs text-gray-400 mt-0.5">{user.company}</div>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={() => {
              setIsAddSubscriptionModalOpen(false);
              // Reset form
              setNewSubscription({
                companyName: '',
                contactPersonName: '',
                emailAddress: '',
                contactTelephone: '',
                subscriptionTier: '',
                startDate: '',
                endDate: '',
                amountPerMonth: '',
                autoRenew: '',
                selectedUserIds: []
              });
              setUserSearchTerm('');
            }}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleCreateSubscription}
            className="px-4 py-2 bg-[#FF5722] text-white rounded-md hover:bg-[#FF5722]/90"
          >
            Create Subscription
          </button>
        </div>
      </div>
    </div>
  );

  const renderAddTierModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">{selectedTier ? 'Edit Subscription Tier' : 'Add New Subscription Tier'}</h2>
          <button
            onClick={() => {
              setIsAddModalOpen(false);
              setSelectedTier(null);
            }}
            className="text-gray-500 hover:text-gray-700"
          >
            <XCircleIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Basic Information */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tier Name
            </label>
            <input
              type="text"
              value={newTier.name}
              onChange={(e) => setNewTier({ ...newTier, name: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              placeholder="e.g., Enterprise"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subscription Price
            </label>
            <input
              type="text"
              value={newTier.subscriptionPrice}
              onChange={(e) => setNewTier({ ...newTier, subscriptionPrice: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              placeholder="e.g., $299"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SMS Credits (Annual)
            </label>
            <input
              type="text"
              value={newTier.smsCreditsAnnual}
              onChange={(e) => setNewTier({ ...newTier, smsCreditsAnnual: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              placeholder="e.g., 10000"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional SMS Credits (Annual)
            </label>
            <input
              type="text"
              value={newTier.additionalSmsCreditsAnnual}
              onChange={(e) => setNewTier({ ...newTier, additionalSmsCreditsAnnual: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              placeholder="e.g., $0.05"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional SMS Credits (Monthly)
            </label>
            <input
              type="text"
              value={newTier.additionalSmsCreditsMonthly}
              onChange={(e) => setNewTier({ ...newTier, additionalSmsCreditsMonthly: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              placeholder="e.g., $0.05"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              One Time Setup Fee
            </label>
            <input
              type="text"
              value={newTier.oneTimeSetupFee}
              onChange={(e) => setNewTier({ ...newTier, oneTimeSetupFee: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              placeholder="e.g., $25"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message Templates
            </label>
            <input
              type="text"
              value={newTier.messageTemplates}
              onChange={(e) => setNewTier({ ...newTier, messageTemplates: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              placeholder="e.g., 10 or Unlimited"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Users
            </label>
            <input
              type="text"
              value={newTier.users}
              onChange={(e) => setNewTier({ ...newTier, users: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              placeholder="e.g., 5 or Unlimited"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Support Level
            </label>
            <select
              value={newTier.support}
              onChange={(e) => setNewTier({ ...newTier, support: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="Email">Email</option>
              <option value="Priority">Priority</option>
              <option value="Premium">Premium</option>
              <option value="Dedicated">Dedicated</option>
            </select>
          </div>
        </div>

        {/* Custom Fields Section */}
        <div className="mt-6 border-t pt-6">
          <h3 className="text-lg font-medium mb-4">Custom Fields</h3>
          
          {/* Display existing custom fields */}
          {newTier.customFields.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Added Custom Fields:</h4>
              <div className="space-y-2">
                {newTier.customFields.map((field, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
                    <div>
                      <span className="font-medium">{field.name}:</span> {field.value}
                    </div>
                    <button
                      onClick={() => handleRemoveCustomField(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Add new custom field form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Field Name
              </label>
              <input
                type="text"
                value={newCustomField.name}
                onChange={(e) => setNewCustomField({ ...newCustomField, name: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="e.g., Custom Feature"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Field Value
              </label>
              <input
                type="text"
                value={newCustomField.value}
                onChange={(e) => setNewCustomField({ ...newCustomField, value: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="e.g., Value or description"
              />
            </div>
            <div className="md:col-span-2">
              <button
                onClick={handleAddCustomField}
                className="w-full px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 flex items-center justify-center"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                Add Custom Field
              </button>
            </div>
          </div>
        </div>

        {/* Features Checkboxes */}
        <div className="mt-6">
          <h3 className="text-lg font-medium mb-4">Features</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={newTier.singleSmsMessages}
                onChange={(e) => setNewTier({ ...newTier, singleSmsMessages: e.target.checked })}
                className="h-4 w-4 text-[#FF5722] rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Single SMS Messages</span>
            </label>

            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={newTier.bulkCampaigns}
                onChange={(e) => setNewTier({ ...newTier, bulkCampaigns: e.target.checked })}
                className="h-4 w-4 text-[#FF5722] rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Bulk Campaigns</span>
            </label>

            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={newTier.customSenderId}
                onChange={(e) => setNewTier({ ...newTier, customSenderId: e.target.checked })}
                className="h-4 w-4 text-[#FF5722] rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Custom Sender ID</span>
            </label>

            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={newTier.sharedTemplates}
                onChange={(e) => setNewTier({ ...newTier, sharedTemplates: e.target.checked })}
                className="h-4 w-4 text-[#FF5722] rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Shared Templates</span>
            </label>

            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={newTier.sharedContacts}
                onChange={(e) => setNewTier({ ...newTier, sharedContacts: e.target.checked })}
                className="h-4 w-4 text-[#FF5722] rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Shared Contacts</span>
            </label>

            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={newTier.scheduledMessages}
                onChange={(e) => setNewTier({ ...newTier, scheduledMessages: e.target.checked })}
                className="h-4 w-4 text-[#FF5722] rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Scheduled Messages</span>
            </label>

            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={newTier.recurringMessages}
                onChange={(e) => setNewTier({ ...newTier, recurringMessages: e.target.checked })}
                className="h-4 w-4 text-[#FF5722] rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Recurring Messages</span>
            </label>

            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={newTier.usageReporting}
                onChange={(e) => setNewTier({ ...newTier, usageReporting: e.target.checked })}
                className="h-4 w-4 text-[#FF5722] rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Usage Reporting</span>
            </label>

            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={newTier.scheduledReports}
                onChange={(e) => setNewTier({ ...newTier, scheduledReports: e.target.checked })}
                className="h-4 w-4 text-[#FF5722] rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Scheduled Reports</span>
            </label>

            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={newTier.apiAccess}
                onChange={(e) => setNewTier({ ...newTier, apiAccess: e.target.checked })}
                className="h-4 w-4 text-[#FF5722] rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">API Access</span>
            </label>
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={() => {
              setIsAddModalOpen(false);
              setSelectedTier(null);
              // Reset form
              setNewTier({
                name: '',
                subscriptionPrice: '',
                smsCreditsAnnual: '',
                singleSmsMessages: true,
                bulkCampaigns: true,
                additionalSmsCreditsAnnual: '',
                additionalSmsCreditsMonthly: '',
                oneTimeSetupFee: '',
                customSenderId: false,
                messageTemplates: '',
                users: '',
                sharedTemplates: false,
                sharedContacts: false,
                scheduledMessages: false,
                recurringMessages: false,
                usageReporting: true,
                scheduledReports: true,
                apiAccess: true,
                support: 'Email',
                customFields: []
              });
            }}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={selectedTier ? handleSaveEditTier : handleAddTier}
            className="px-4 py-2 bg-[#FF5722] text-white rounded-md hover:bg-[#FF5722]/90"
          >
            {selectedTier ? 'Save Changes' : 'Add Tier'}
          </button>
        </div>
      </div>
    </div>
  );

  const renderEditSubscriptionModal = () => {
    if (!editedSubscription) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Edit Subscription</h2>
            <button
              onClick={() => {
                setIsEditModalOpen(false);
                setSelectedSubscription(null);
                setEditedSubscription(null);
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              <XCircleIcon className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Customer Name
              </label>
              <input
                type="text"
                value={editedSubscription.customerName}
                onChange={(e) => setEditedSubscription({
                  ...editedSubscription,
                  customerName: e.target.value
                })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subscription Tier
              </label>
              <select
                value={editedSubscription.tier}
                onChange={(e) => setEditedSubscription({
                  ...editedSubscription,
                  tier: e.target.value
                })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                {tiers.map(tier => {
                  const price = parseFloat(tier.priceMonthly.replace('$', '').replace(',', '')) || 0;
                  return (
                    <option key={tier.id} value={tier.name}>
                      {tier.name} - {tier.priceMonthly}/month
                    </option>
                  );
                })}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={editedSubscription.status}
                onChange={(e) => setEditedSubscription({
                  ...editedSubscription,
                  status: e.target.value as 'active' | 'inactive' | 'pending' | 'cancelled'
                })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="pending">Pending</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={editedSubscription.startDate}
                  onChange={(e) => setEditedSubscription({
                    ...editedSubscription,
                    startDate: e.target.value
                  })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={editedSubscription.endDate}
                  onChange={(e) => setEditedSubscription({
                    ...editedSubscription,
                    endDate: e.target.value
                  })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-500">$</span>
                <input
                  type="number"
                  value={editedSubscription.amount}
                  onChange={(e) => setEditedSubscription({
                    ...editedSubscription,
                    amount: parseFloat(e.target.value)
                  })}
                  className="w-full border border-gray-300 rounded-md pl-8 pr-3 py-2"
                  step="0.01"
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="autoRenew"
                checked={editedSubscription.autoRenew}
                onChange={(e) => setEditedSubscription({
                  ...editedSubscription,
                  autoRenew: e.target.checked
                })}
                className="h-4 w-4 text-[#FF5722] rounded border-gray-300"
              />
              <label htmlFor="autoRenew" className="ml-2 block text-sm text-gray-700">
                Auto-renew subscription
              </label>
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={() => {
                setIsEditModalOpen(false);
                setSelectedSubscription(null);
                setEditedSubscription(null);
              }}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveSubscription}
              className="px-4 py-2 bg-[#FF5722] text-white rounded-md hover:bg-[#FF5722]/90"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderSubscriptionsTab = () => {
    if (loadingSubscriptions) {
      return (
        <div className="bg-white shadow-md rounded-lg overflow-hidden p-12 text-center">
          <p className="text-gray-600">Loading subscriptions...</p>
        </div>
      );
    }

    if (errorSubscriptions) {
      return (
        <div className="bg-white shadow-md rounded-lg overflow-hidden p-12 text-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 font-medium mb-2">{errorSubscriptions}</p>
          <p className="text-sm text-gray-500 mb-4">
            {errorSubscriptions.includes('Unauthorized') && 'Please check your login credentials.'}
            {errorSubscriptions.includes('Forbidden') && 'You need super admin privileges to access subscriptions.'}
            {errorSubscriptions.includes('404') && 'The subscriptions endpoint may not be configured correctly.'}
            {errorSubscriptions.includes('Server error') && 'Please check if the API server is running.'}
          </p>
          <button
            onClick={() => fetchSubscriptions()}
            className="px-4 py-2 bg-[#FF5722] text-white rounded-md hover:bg-[#FF5722]/90"
          >
            Retry
          </button>
        </div>
      );
    }

    return (
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-lg font-medium">Active Subscriptions</h2>
          <button
            onClick={() => setIsAddSubscriptionModalOpen(true)}
            className="flex items-center px-4 py-2 bg-[#FF5722] text-white rounded-md hover:bg-[#FF5722]/90"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Add New Subscription
          </button>
        </div>
        <div className="overflow-x-auto">
          {subscriptions.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <p>No subscriptions found.</p>
              <button
                onClick={() => setIsAddSubscriptionModalOpen(true)}
                className="mt-4 px-4 py-2 bg-[#FF5722] text-white rounded-md hover:bg-[#FF5722]/90"
              >
                Create Your First Subscription
              </button>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">End Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Auto-Renew</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {subscriptions.map((subscription) => (
              <tr key={subscription.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {subscription.customerName}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {subscription.tier}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(subscription.status)}`}>
                    {subscription.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {subscription.startDate}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {subscription.endDate}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  ${typeof subscription.amount === 'number' ? subscription.amount.toFixed(2) : subscription.amount}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {subscription.autoRenew ? (
                    <CheckCircleIcon className="w-5 h-5 text-green-500" />
                  ) : (
                    <XCircleIcon className="w-5 h-5 text-red-500" />
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditSubscription(subscription)}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      <PencilIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={async () => {
                        const confirmed = window.confirm('Are you sure you want to delete this subscription? This action cannot be undone.');
                        if (!confirmed) return;
                        try {
                          await apiDeleteSubscription(subscription.id, { userId: subscription.userId || undefined, plan: subscription.tier });
                          // Optimistic update: remove locally
                          setSubscriptions(prev => prev.filter(s => s.id !== subscription.id));
                        } catch (err: any) {
                          console.error('Failed to delete subscription:', err);
                          alert(err?.message || 'Failed to delete subscription');
                        }
                      }}
                      className="text-red-600 hover:text-red-900"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                </td>
              </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  };

  const handleEditTier = async (tier: ApiSubscriptionTier) => {
    setSelectedTier(tier);
    // Populate form with tier data
    setNewTier({
      name: tier.name,
      subscriptionPrice: tier.priceMonthly,
      smsCreditsAnnual: tier.smsCreditsAnnual,
      singleSmsMessages: tier.singleSmsMessages,
      bulkCampaigns: tier.bulkCampaigns,
      additionalSmsCreditsAnnual: tier.additionalSmsCreditPriceAnnual,
      additionalSmsCreditsMonthly: tier.additionalSmsCreditPriceMonthly,
      oneTimeSetupFee: tier.oneTimeSetupFee,
      customSenderId: tier.customSenderId,
      messageTemplates: tier.messageTemplates,
      users: tier.users,
      sharedTemplates: tier.sharedTemplates,
      sharedContacts: tier.sharedContacts,
      scheduledMessages: tier.scheduledMessages,
      recurringMessages: tier.recurringMessages,
      usageReporting: tier.usageReporting,
      scheduledReports: tier.scheduledReports,
      apiAccess: tier.apiAccess,
      support: tier.support,
      customFields: tier.featuresJson || []
    });
    setIsAddModalOpen(true);
  };

  const handleSaveEditTier = async () => {
    if (!selectedTier) return;
    
    try {
      const tierData: CreateSubscriptionTierInput = {
        name: newTier.name,
        priceMonthly: newTier.subscriptionPrice,
        smsCreditsAnnual: newTier.smsCreditsAnnual,
        additionalSmsCreditPriceAnnual: newTier.additionalSmsCreditsAnnual,
        additionalSmsCreditPriceMonthly: newTier.additionalSmsCreditsMonthly,
        oneTimeSetupFee: newTier.oneTimeSetupFee,
        messageTemplates: newTier.messageTemplates,
        users: newTier.users,
        support: newTier.support,
        singleSmsMessages: newTier.singleSmsMessages,
        bulkCampaigns: newTier.bulkCampaigns,
        customSenderId: newTier.customSenderId,
        sharedTemplates: newTier.sharedTemplates,
        sharedContacts: newTier.sharedContacts,
        scheduledMessages: newTier.scheduledMessages,
        recurringMessages: newTier.recurringMessages,
        usageReporting: newTier.usageReporting,
        scheduledReports: newTier.scheduledReports,
        apiAccess: newTier.apiAccess,
        featuresJson: newTier.customFields.length > 0 ? newTier.customFields : null,
        sortIndex: selectedTier.sortIndex,
        status: selectedTier.status
      };

      await updateTier(selectedTier.id, tierData);
      setIsAddModalOpen(false);
      setSelectedTier(null);
      
      // Refresh tiers list
      await fetchTiers();
      
      // Reset form
      setNewTier({
        name: '',
        subscriptionPrice: '',
        smsCreditsAnnual: '',
        singleSmsMessages: true,
        bulkCampaigns: true,
        additionalSmsCreditsAnnual: '',
        additionalSmsCreditsMonthly: '',
        oneTimeSetupFee: '',
        customSenderId: false,
        messageTemplates: '',
        users: '',
        sharedTemplates: false,
        sharedContacts: false,
        scheduledMessages: false,
        recurringMessages: false,
        usageReporting: true,
        scheduledReports: true,
        apiAccess: true,
        support: 'Email',
        customFields: []
      });
    } catch (err) {
      console.error('Failed to update tier:', err);
      alert('Failed to update subscription tier. Please try again.');
    }
  };

  const handleDeleteTier = async (tierId: string) => {
    if (!window.confirm('Are you sure you want to delete this tier? It will be deactivated.')) {
      return;
    }

    try {
      await deleteTier(tierId);
      await fetchTiers();
    } catch (err) {
      console.error('Failed to delete tier:', err);
      alert('Failed to delete subscription tier. Please try again.');
    }
  };

  const renderTiersTab = () => {
    if (loadingTiers) {
      return (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <p className="text-gray-600">Loading subscription tiers...</p>
        </div>
      );
    }

    if (errorTiers) {
      return (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <p className="text-red-600">{errorTiers}</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-medium">Subscription Tiers</h2>
          <button
            onClick={() => {
              setSelectedTier(null);
              setIsAddModalOpen(true);
            }}
            className="flex items-center px-4 py-2 bg-[#FF5722] text-white rounded-md hover:bg-[#FF5722]/90"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Add New Tier
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {tiers.map((tier) => {
            const price = parseFloat(tier.priceMonthly.replace('$', '').replace(',', '')) || 0;
            const features: string[] = [];
            if (tier.smsCreditsAnnual) features.push(`${tier.smsCreditsAnnual} SMS credits`);
            if (tier.users) features.push(`${tier.users} users`);
            if (tier.support) features.push(`${tier.support} support`);
            if (tier.apiAccess) features.push('API access');
            if (tier.customSenderId) features.push('Custom Sender ID');
            if (tier.scheduledMessages) features.push('Scheduled Messages');
            if (tier.recurringMessages) features.push('Recurring Messages');

            return (
              <div key={tier.id} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{tier.name}</h3>
                    <p className="text-2xl font-bold text-[#FF5722] mt-2">
                      {tier.priceMonthly}
                      <span className="text-sm text-gray-500">/month</span>
                    </p>
                  </div>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(tier.status)}`}>
                    {tier.status}
                  </span>
                </div>
                <ul className="mt-6 space-y-4">
                  {features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                      <span className="text-sm text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 flex space-x-2">
                  <button
                    onClick={() => handleEditTier(tier)}
                    className="flex-1 px-4 py-2 bg-white text-[#FF5722] border border-[#FF5722] rounded-md hover:bg-[#FF5722] hover:text-white transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteTier(tier.id)}
                    className="flex-1 px-4 py-2 bg-white text-red-600 border border-red-600 rounded-md hover:bg-red-600 hover:text-white transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderSettingsTab = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-lg font-medium mb-6">Subscription Settings</h2>
        {settingsLoading ? (
          <p className="text-gray-500">Loading settings...</p>
        ) : settingsError ? (
          <p className="text-red-600">{settingsError}</p>
        ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Billing Cycle
            </label>
            <select 
              value={settings.defaultBillingCycle === 'annual' ? 'yearly' : settings.defaultBillingCycle}
              onChange={(e) => setSettings(s => ({ ...s, defaultBillingCycle: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent">
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Grace Period (days)
            </label>
            <input
              type="number"
              value={settings.gracePeriodDays}
              onChange={(e) => setSettings(s => ({ ...s, gracePeriodDays: Number(e.target.value) }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Auto-Renewal Default
            </label>
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="autoRenewal"
                  value="enabled"
                  checked={settings.autoRenewalDefault === 'enabled'}
                  onChange={() => setSettings(s => ({ ...s, autoRenewalDefault: 'enabled' }))}
                  className="mr-2"
                />
                Enabled
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="autoRenewal"
                  value="disabled"
                  checked={settings.autoRenewalDefault === 'disabled'}
                  onChange={() => setSettings(s => ({ ...s, autoRenewalDefault: 'disabled' }))}
                  className="mr-2"
                />
                Disabled
              </label>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Gateway
            </label>
            <select 
              value={settings.paymentGateway}
              onChange={(e) => setSettings(s => ({ ...s, paymentGateway: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent">
              <option value="stripe">Stripe</option>
              <option value="paypal">PayPal</option>
              <option value="square">Square</option>
            </select>
          </div>
        </div>
        )}
        <div className="mt-6">
          <button 
            onClick={async () => {
              try {
                setSettingsLoading(true);
                const updated = await apiUpdateSettings(settings);
                setSettings(updated);
                alert('Settings saved successfully');
              } catch (e: any) {
                console.error('Save settings failed:', e);
                alert(e?.message || 'Failed to save settings');
              } finally {
                setSettingsLoading(false);
              }
            }}
            className="px-4 py-2 bg-[#FF5722] text-white rounded-md hover:bg-[#FF5722]/90">
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );

  const renderAnalyticsTab = () => (
    <div className="space-y-6">
      {analyticsLoading ? (
        <div className="bg-white rounded-lg shadow-md p-6 text-gray-500">Loading analytics...</div>
      ) : analyticsError ? (
        <div className="bg-white rounded-lg shadow-md p-6 text-red-600">{analyticsError}</div>
      ) : (
      <>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">MRR</h3>
            <p className="text-3xl font-bold text-[#FF5722]">${analytics.mrr.toFixed(2)}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Active Subscriptions</h3>
            <p className="text-3xl font-bold text-[#FF5722]">{analytics.activeSubscriptions}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Churn Rate</h3>
            <p className="text-3xl font-bold text-[#FF5722]">{analytics.churnRate.toFixed(2)}%</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">ARR</h3>
            <p className="text-3xl font-bold text-[#FF5722]">${analytics.arr.toFixed(2)}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Total Subscriptions</h3>
            <p className="text-3xl font-bold text-[#FF5722]">{analytics.totalSubscriptions}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Avg Subscription Value</h3>
            <p className="text-3xl font-bold text-[#FF5722]">${analytics.averageSubscriptionValue.toFixed(2)}</p>
          </div>
        </div>
      </>
      )}
    </div>
  );

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Subscription Management</h1>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('subscriptions')}
            className={`${
              activeTab === 'subscriptions'
                ? 'border-[#FF5722] text-[#FF5722]'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <UserGroupIcon className="w-5 h-5 mr-2" />
            Subscriptions
          </button>
          <button
            onClick={() => setActiveTab('tiers')}
            className={`${
              activeTab === 'tiers'
                ? 'border-[#FF5722] text-[#FF5722]'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <CreditCardIcon className="w-5 h-5 mr-2" />
            Subscription Tiers
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`${
              activeTab === 'settings'
                ? 'border-[#FF5722] text-[#FF5722]'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <CogIcon className="w-5 h-5 mr-2" />
            Settings
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`${
              activeTab === 'analytics'
                ? 'border-[#FF5722] text-[#FF5722]'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <ChartBarIcon className="w-5 h-5 mr-2" />
            Analytics
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'subscriptions' && renderSubscriptionsTab()}
      {activeTab === 'tiers' && renderTiersTab()}
      {activeTab === 'settings' && renderSettingsTab()}
      {activeTab === 'analytics' && renderAnalyticsTab()}
      {isAddModalOpen && renderAddTierModal()}
      {isAddSubscriptionModalOpen && renderAddSubscriptionModal()}
      {isEditModalOpen && renderEditSubscriptionModal()}
    </div>
  );
};

export default SubscriptionManagement; 